/**
 * VisionAI API client v1.4
 */

const BASE = '/api/v1';

export function pickupSSOToken() {
  const h = window.location.hash.slice(1);
  if (h.startsWith('token=')) {
    const t = new URLSearchParams(h).get('token');
    if (t) { localStorage.setItem('vai_token', t); window.history.replaceState(null, '', window.location.pathname); return t; }
  }
  return null;
}
export const getToken        = ()  => localStorage.getItem('vai_token');
export const setToken        = (t) => localStorage.setItem('vai_token', t);
export const clearToken      = ()  => localStorage.removeItem('vai_token');
export const isAuthenticated = ()  => !!getToken();

export function redirectToSSO() {
  const url = window.__ENV__?.SSO_LOGIN_URL || import.meta.env?.VITE_SSO_LOGIN_URL;
  if (url) window.location.href = `${url}?redirect_uri=${encodeURIComponent(window.location.origin + '/api/v1/auth/sso/callback')}`;
}

async function req(method, path, body = null) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
  if (res.status === 401) { clearToken(); window.dispatchEvent(new Event('vai:unauthenticated')); throw Object.assign(new Error('Session expired'), { status: 401 }); }
  if (res.status === 204) return null;
  const data = await res.json();
  if (!res.ok) { const err = new Error(data.message || `HTTP ${res.status}`); err.status = res.status; err.errors = data.errors; err.tokenBudget = data.tokenBudget === true; throw err; }
  return data;
}

const get   = (path, p) => req('GET',   path + (p && Object.keys(p).filter(k=>p[k]!==undefined&&p[k]!==''&&p[k]!==null).length ? '?' + new URLSearchParams(p) : ''));
const post  = (path, b) => req('POST',  path, b);
const patch = (path, b) => req('PATCH', path, b);
const del   = (path)    => req('DELETE',path);

export const auth = {
  me:           ()  => get('/auth/me'),
  forgotPassword: (b) => post('/auth/forgot-password', b),
  demoRequest:    (b) => post('/auth/demo-request', b),
  devLogin:     (b) => post('/auth/dev-login', b),
  login:        (b) => post('/auth/login', b),
  // clear the token and cookie, then redirect to logout endpoint which will handle SSO logout if applicable.Also clears the docs cookie to ensure the user is fully logged out from the UI.
  logout:       ()  => { clearToken();document.cookie = "vai_docs=; path=/docs; SameSite=Lax; Max-Age=0"; window.location.href = '/api/v1/auth/logout'; },
  logoutLocal:  ()  => { clearToken(); document.cookie = "vai_docs=; path=/docs; SameSite=Lax; Max-Age=0";window.location.href = '/'; },
};
export const tenants = {
  list:   (p={}) => get('/tenants', p),
  get:    (id)   => get(`/tenants/${id}`),
  create: (b)    => post('/tenants', b),
  update: (id,b) => patch(`/tenants/${id}`, b),
  delete: (id)   => del(`/tenants/${id}`),
};
export const workflows = {
  list:   (p={}) => get('/workflows', p),
  get:    (id)   => get(`/workflows/${id}`),
  create: (b)    => post('/workflows', b),
  update: (id,b) => patch(`/workflows/${id}`, b),
  delete: (id)   => del(`/workflows/${id}`),
  clone:  (id,b={}) => post(`/workflows/${id}/clone`, b),
  sync:   ()     => post('/workflows/sync', {}),
};
export const configs = {
  list:          (p={}) => get('/configs', p),
  get:           (id)   => get(`/configs/${id}`),
  create:        (b)    => post('/configs', b),
  update:        (id,b) => patch(`/configs/${id}`, b),
  delete:        (id)   => del(`/configs/${id}`),
  promptHistory: (id)   => get(`/configs/${id}/prompt-history`),
};
export const runs = {
  submit:       (b)    => post('/runs', b),
  list:         (p={}) => get('/runs', p),
  get:          (id)   => get(`/runs/${id}`),
  summary:      (p={}) => get('/runs/stats/summary', p),
  byParam:      (p={}) => get('/runs/stats/by-param', p),
  topConfigs:   (p={}) => get('/runs/stats/top-configs', p),
  report:       (p={}) => get('/runs/report', p),
  analytics:    (p={}) => get('/runs/analytics', p),
  reportCsvUrl: (p={}) => {
    const qs = new URLSearchParams({ ...p, format:'csv' }).toString();
    return `${BASE}/runs/report?${qs}`;
  },
};
export const providers = {
  list:   ()     => get('/providers'),
  create: (b)    => post('/providers', b),
  update: (id,b) => patch(`/providers/${id}`, b),
  remove: (id)   => del(`/providers/${id}`),
  test:   (id)   => post(`/providers/${id}/test`, {}),
};
export const promptLibrary = {
  list:   (p={}) => get('/prompt-library', p),
  get:    (id)   => get(`/prompt-library/${id}`),
  create: (b)    => post('/prompt-library', b),
  update: (id,b) => patch(`/prompt-library/${id}`, b),
  delete: (id)   => del(`/prompt-library/${id}`),
};
export const prompts = { test: (b) => post('/prompts/test', b) };
export const apiKeys = {
  list:   ()   => get('/api-keys'),
  create: (b)  => post('/api-keys', b),
  revoke: (id) => del(`/api-keys/${id}`),
};
export const audit = { list: (p={}) => get('/audit', p) };
export const builder = {
  generate:      (b) => post('/builder/generate', b),
  testOnSamples: (b) => post('/builder/test-on-samples', b),
};
export const users = {
  list:           (p={}) => get('/users', p),
  create:         (b)    => post('/users', b),
  update:         (id,b) => patch(`/users/${id}`, b),
  deactivate:     (id, hard=false) => req('DELETE', `/users/${id}${hard?'?hard=true':''}`),
  resetPassword:  (id)   => post(`/users/${id}/reset-password`, {}),
  changePassword: (b)    => post('/users/me/change-password', b),
  getManagedTenants:  (id) => get(`/users/${id}/managed-tenants`),
  setManagedTenants:  (id,b) => req('PUT', `/users/${id}/managed-tenants`, b),
  myManagedTenants:   ()    => get('/users/me/managed-tenants'),
};
export const tokenBudgets = {
  list:          ()         => get('/token-budgets'),
  get:           (tid)      => get(`/token-budgets/${tid}`),
  save:          (tid, b)   => req('PUT', `/token-budgets/${tid}`, b),
  remove:        (tid)      => req('DELETE', `/token-budgets/${tid}`),
  usage:         (tid, p={})=> get(`/token-budgets/${tid}/usage`, p),
  topUp:         (tid, b)   => post(`/token-budgets/${tid}/top-up`, b),
  topUps:        (tid)      => get(`/token-budgets/${tid}/top-ups`),
  editTopUp:     (id, b)    => req('PATCH',  `/token-budgets/top-ups/${id}`, b),
  deleteTopUp:   (id)       => req('DELETE', `/token-budgets/top-ups/${id}`),
  expireTopUp:   (id)       => post(`/token-budgets/top-ups/${id}/expire`, {}),
};
export const feedback = {
  submit:         (b)  => post('/feedback', b),
  getForRun:      (id) => get(`/feedback/${id}`),
  statsForConfig: (id) => get(`/feedback/stats/${id}`),
};
export const llmLogs = { list: (p={}) => get('/llm-logs', p) };

export const solutions = {
  // configs
  list:          (p={})     => get('/solutions', p),
  get:           (id)       => get(`/solutions/${id}`),
  create:        (b)        => post('/solutions', b),
  update:        (id, b)    => req('PATCH', `/solutions/${id}`, b),
  remove:        (id)       => req('DELETE', `/solutions/${id}`),
  clone:         (id, b={}) => post(`/solutions/${id}/clone`, b),
  // templates
  templates:     ()         => get('/solutions/templates'),
  // run
  run:           (id, b)    => post(`/solutions/${id}/run`, b),
  // solution runs
  runs:          (p={})     => get('/solutions/runs/list', p),
  getRun:        (id)       => get(`/solutions/runs/${id}`),
  runsExportUrl: (p={})     => {
    const qs = new URLSearchParams(p).toString();
    return `${BASE}/solutions/runs/export${qs ? '?' + qs : ''}`;
  },
  runsReport:    (p={})     => get('/solutions/runs/report', p),
  runsReportCsvUrl: (p={})  => {
    const qs = new URLSearchParams({ ...p, format:'csv' }).toString();
    return `${BASE}/solutions/runs/report?${qs}`;
  },
};

export default {
  auth, tenants, workflows, configs, runs, users, tokenBudgets, solutions,
  providers, promptLibrary, prompts, apiKeys, audit, builder, feedback, llmLogs,
  getToken, setToken, clearToken, isAuthenticated, pickupSSOToken, redirectToSSO,
};

export const fieldLinks = {
  list:   ()     => get('/field-links'),
  create: (b)    => post('/field-links', b),
  revoke: (id)   => del(`/field-links/${id}`),
  verify: (token)=> get('/field-links/verify', { token }),
};
